export const webPushNotification = async ({
    isSupported,
    permission,
    notification,
    translate,
}: {
    isSupported: boolean;
    permission: string;
    notification: { [key: string]: string };
    translate: (key: string) => string;
}) => {
    if (
        isSupported &&
        permission === 'granted' &&
        'serviceWorker' in navigator
    ) {
        try {
            const registrations =
                await navigator.serviceWorker.getRegistrations();

            if (registrations.length === 0) {
                try {
                    const newRegistration =
                        await navigator.serviceWorker.register(
                            '/service-worker.js'
                        );

                    if (newRegistration.installing) {
                        await new Promise<void>((resolve) => {
                            const installingWorker = newRegistration.installing;
                            if (installingWorker) {
                                installingWorker.addEventListener(
                                    'statechange',
                                    () => {
                                        if (
                                            installingWorker.state ===
                                                'installed' ||
                                            installingWorker.state ===
                                                'activated'
                                        ) {
                                            resolve();
                                        }
                                    }
                                );
                            } else {
                                resolve();
                            }
                        });
                    }
                } catch (regError) {
                    throw new Error(translate('Errors.NoWebPushSupport'));
                }
            }

            const timeoutPromise = new Promise<never>((_, reject) => {
                setTimeout(() => {
                    reject(
                        new Error(
                            translate('Errors.TimeoutWaitingForServiceWorker')
                        )
                    );
                }, 5000);
            });

            const readyPromise = navigator.serviceWorker.ready;

            let registration: ServiceWorkerRegistration;
            try {
                registration = await Promise.race([
                    readyPromise,
                    timeoutPromise,
                ]);
            } catch (error) {
                const regs = await navigator.serviceWorker.getRegistrations();
                if (regs.length > 0) {
                    registration = regs[0];
                } else {
                    throw new Error(translate('Errors.NoWebPushSupport'));
                }
            }

            if (!registration) {
                throw new Error(translate('Errors.NoWebPushSupport'));
            }

            const title =
                translate(notification.message) ||
                translate('Notifications.NewNotification');
            const body = translate(notification.message) || '';

            try {
                const notificationOptions: NotificationOptions = {
                    body: body,
                    icon: '/warbox.ico',
                    badge: '/warbox.ico',
                    tag: `lead-${notification.lead}`,
                    requireInteraction: false,
                    silent: false,
                    data: {
                        url: `/management/leads/${notification.lead}`,
                        lead: notification.lead,
                    },
                };

                const isPageVisible = !document.hidden;

                let notificationShown = false;
                try {
                    const notificationPromise = registration.showNotification(
                        title,
                        notificationOptions
                    );

                    await notificationPromise;
                    notificationShown = true;
                } catch (swError) {
                    throw swError;
                }

                if (
                    !notificationShown &&
                    !isPageVisible &&
                    'Notification' in window
                ) {
                    try {
                        const directNotification = new Notification(title, {
                            body: body,
                            icon: '/static/warbox_logo.png',
                            badge: '/static/warbox_logo.png',
                            tag: `lead-${notification.lead}`,
                            requireInteraction: false,
                            silent: false,
                            data: {
                                url: `/management/leads/${notification.lead}`,
                                lead: notification.lead,
                            },
                        });

                        directNotification.onclick = (event) => {
                            event.preventDefault();
                            window.focus();
                            if (notificationOptions.data?.url) {
                                window.location.href =
                                    notificationOptions.data.url;
                            }
                            directNotification.close();
                        };

                        notificationShown = true;
                    } catch (directError) {
                        throw directError;
                    }
                }
            } catch (showError) {
                throw showError;
            }
        } catch (pushError) {
            throw pushError;
        }
    }
};
